import { RelationshipLagCalendar, } from '../models/Schedule';
import { Calendar } from './Calendar';
import DateUtils from './DateUtils';
import { DepthFirstGraphSort } from './DepthFirstGraphSort';
export class CpmException extends Error {
    constructor(message) {
        super(message || 'CPM calculation error');
        this.name = 'CpmException';
    }
}
export var ActivityType;
(function (ActivityType) {
    ActivityType["TASK_DEPENDENT"] = "TASK_DEPENDENT";
    ActivityType["RESOURCE_DEPENDENT"] = "RESOURCE_DEPENDENT";
    ActivityType["LEVEL_OF_EFFORT"] = "LEVEL_OF_EFFORT";
    ActivityType["START_MILESTONE"] = "START_MILESTONE";
    ActivityType["FINISH_MILESTONE"] = "FINISH_MILESTONE";
    ActivityType["WBS_SUMMARY"] = "WBS_SUMMARY";
    ActivityType["HAMMOCK"] = "HAMMOCK";
    ActivityType["START_FLAG"] = "START_FLAG";
    ActivityType["FINISH_FLAG"] = "FINISH_FLAG";
})(ActivityType || (ActivityType = {}));
export var ConstraintType;
(function (ConstraintType) {
    ConstraintType["AS_SOON_AS_POSSIBLE"] = "AS_SOON_AS_POSSIBLE";
    ConstraintType["AS_LATE_AS_POSSIBLE"] = "AS_LATE_AS_POSSIBLE";
    ConstraintType["START_NO_EARLIER_THAN"] = "START_NO_EARLIER_THAN";
    ConstraintType["START_NO_LATER_THAN"] = "START_NO_LATER_THAN";
    ConstraintType["FINISH_NO_EARLIER_THAN"] = "FINISH_NO_EARLIER_THAN";
    ConstraintType["FINISH_NO_LATER_THAN"] = "FINISH_NO_LATER_THAN";
    ConstraintType["MUST_START_ON"] = "MUST_START_ON";
    ConstraintType["MUST_FINISH_ON"] = "MUST_FINISH_ON";
    ConstraintType["START_ON"] = "START_ON";
    ConstraintType["FINISH_ON"] = "FINISH_ON";
})(ConstraintType || (ConstraintType = {}));
export var MpxjResourceType;
(function (MpxjResourceType) {
    MpxjResourceType["MATERIAL"] = "MATERIAL";
    MpxjResourceType["WORK"] = "WORK";
    MpxjResourceType["COST"] = "COST";
})(MpxjResourceType || (MpxjResourceType = {}));
export var RelationshipType;
(function (RelationshipType) {
    RelationshipType["FINISH_START"] = "FS";
    RelationshipType["START_START"] = "SS";
    RelationshipType["FINISH_FINISH"] = "FF";
    RelationshipType["START_FINISH"] = "SF";
})(RelationshipType || (RelationshipType = {}));
export var PercentCompleteType;
(function (PercentCompleteType) {
    PercentCompleteType["DURATION"] = "DURATION";
    PercentCompleteType["PHYSICAL"] = "PHYSICAL";
    PercentCompleteType["UNITS"] = "UNITS";
    PercentCompleteType["SCOPE"] = "SCOPE";
})(PercentCompleteType || (PercentCompleteType = {}));
export class TaskDeltas extends Map {
    constructor() {
        super();
    }
}
function isActivity(task) {
    const activityType = task.activity_type?.toUpperCase();
    return !(task.summary ||
        activityType === ActivityType.LEVEL_OF_EFFORT ||
        activityType === ActivityType.WBS_SUMMARY);
}
export class CommonScheduleCalculate extends EventTarget {
    static isActivity(task) {
        return isActivity(task);
    }
    isLevelOfEffortActivity(task) {
        return task.activity_type?.toUpperCase() === ActivityType.LEVEL_OF_EFFORT;
    }
    /**
     * Parse an ISO date required by CPM invariants. Throws if missing/invalid.
     * Use DateUtils.parseISO when absence is a valid state (e.g. must_finish_by).
     */
    requireIso(str, label = 'date') {
        const date = DateUtils.parseISO(str);
        if (date == null) {
            throw new CpmException(`Missing required ${label}`);
        }
        return date;
    }
    requireDate(date, label = 'date') {
        if (date == null) {
            throw new CpmException(`Missing required ${label}`);
        }
        return date;
    }
    //private m_schedule: ScheduleData;
    m_propertyValues;
    m_calendars;
    m_tasks;
    m_resources;
    m_assignments;
    m_dataDate;
    m_projectStartDate;
    m_projectFinishDate;
    m_twentyFourHourCalendar;
    taskDeltas = new TaskDeltas();
    constructor(propertyValues, calendars, tasks, resources, assignments) {
        super();
        this.m_propertyValues = propertyValues;
        const projectStartDate = DateUtils.parseISO(propertyValues.start_date);
        const projectFinishDate = DateUtils.parseISO(propertyValues.finish_date);
        const dataDate = DateUtils.parseISO(propertyValues.status_date);
        if (!projectStartDate)
            throw new Error('project start date is required');
        if (!projectFinishDate)
            throw new Error('project finish date is required');
        if (!dataDate)
            throw new Error('data date is required');
        this.m_projectStartDate = projectStartDate;
        this.m_projectFinishDate = projectFinishDate;
        this.m_dataDate = dataDate;
        this.m_calendars = calendars;
        this.m_tasks = tasks;
        this.m_resources = resources;
        this.m_assignments = assignments;
        this.m_twentyFourHourCalendar = Calendar.getTwentyFourHourCalendar();
    }
    /**
     * Returns task IDs that would form a cycle if a new link were added to/from the given task.
     * Collects all transitive successors and predecessors (only activity tasks).
     */
    static findTasksThatWouldCreateCycle(taskUniqueId, tasks) {
        const taskById = new Map(tasks.map(t => [t.unique_id, t]));
        const task = taskById.get(taskUniqueId);
        if (!task)
            return new Set();
        const result = new Set();
        const traverse = (current, visited, getRelatedIds) => {
            if (visited.has(current.unique_id))
                return;
            visited.add(current.unique_id);
            for (const id of getRelatedIds(current)) {
                result.add(id);
                const next = taskById.get(id);
                if (next && isActivity(next))
                    traverse(next, visited, getRelatedIds);
            }
        };
        const successorIds = (t) => (t.successors || []).map(r => r.successor_task_unique_id).filter(Boolean);
        const predecessorIds = (t) => (t.predecessors || []).map(r => r.predecessor_task_unique_id).filter(Boolean);
        traverse(task, new Set(), successorIds);
        traverse(task, new Set(), predecessorIds);
        return result;
    }
    calculate() {
        if (!this.m_projectStartDate)
            throw new Error('project start date is required');
        if (!this.m_dataDate)
            throw new Error('data date is required');
        if (!this.m_tasks)
            throw new Error('tasks are required');
        if (!this.m_resources)
            throw new Error('resources are required');
        if (!this.m_assignments)
            throw new Error('assignments are required');
        this.taskDeltas.clear();
        const sourceTasks = this.m_tasks;
        // Work on copies so clearDates / remaining-null / critical-null do not mutate Couch docs.
        // Deltas are computed against the original docs, matching Java getDeltas(project, originalTaskNodes).
        this.m_tasks = sourceTasks.map((task) => ({ ...task }));
        const activities = new DepthFirstGraphSort(this.m_tasks, isActivity).sort();
        if (activities.length === 0) {
            this.m_tasks = sourceTasks;
            return new TaskDeltas();
        }
        this.validateActivities(activities);
        // Clear every task, including summaries. Otherwise rollup keeps stale parent start/finish
        // (min/max against the old WBS dates never shrinks them). Matches PrimaveraScheduler.clearDates().
        this.clearDates(this.m_tasks);
        if (this.m_dataDate == null) {
            this.m_dataDate = this.m_projectStartDate;
        }
        else {
            if (this.m_projectStartDate < this.m_dataDate) {
                this.m_projectStartDate = this.m_dataDate;
            }
        }
        this.forwardPass(activities);
        const mustFinishBy = DateUtils.parseISO(this.m_propertyValues.must_finish_by);
        const earlyFinish = activities
            .map((task) => DateUtils.parseISO(task.early_finish))
            .filter((d) => d != null)
            .sort((a, b) => a.getTime() - b.getTime())
            .at(-1);
        if (earlyFinish != null && (mustFinishBy == null || earlyFinish > mustFinishBy)) {
            this.m_projectFinishDate = earlyFinish;
        }
        else if (mustFinishBy != null) {
            this.m_projectFinishDate = mustFinishBy;
        }
        this.backwardPass(activities);
        this.updateDates(activities);
        this.levelOfEffortPass();
        const topLevelTasks = this.m_tasks.filter((task) => this.isTopLevelTask(task));
        topLevelTasks.forEach((task) => this.rollupDatesTask(task));
        this.wbsSummaryPass();
        this.collectDeltas(sourceTasks);
        this.m_tasks = sourceTasks;
        return this.taskDeltas;
    }
    getResourceAssignments(task) {
        return (this.m_assignments || []).filter((assignment) => assignment.task_unique_id === task.unique_id);
    }
    getResourcesForTask(task) {
        const resourceAssignments = this.getResourceAssignments(task);
        return (this.m_resources || []).filter((resource) => resourceAssignments.some((assignment) => assignment.resource_unique_id === resource.unique_id));
    }
    validateActivities(tasks) {
        for (const task of tasks) {
            // Concourse-created tasks omit P6 activity_type; default matches a normal activity.
            const activityType = (task.activity_type ?? ActivityType.TASK_DEPENDENT).toUpperCase();
            const resourceAssignments = this.getResourceAssignments(task);
            if (activityType === ActivityType.RESOURCE_DEPENDENT && resourceAssignments.length > 0) {
                if (resourceAssignments.some((r) => r.work === null || r.work === undefined)) {
                    console.error(`Task has resource assignments without a work value: ${task.name || task._id}`);
                }
                if (resourceAssignments.some((r) => r.remaining_work === null || r.remaining_work === undefined)) {
                    console.error(`Task has resource assignments without a remaining work value: ${task.name || task._id}`);
                }
            }
            else {
                if (task.duration === null || task.duration === undefined) {
                    console.error(`Task has no duration value: ${task.name || task._id}`);
                }
            }
        }
    }
    /**
     * Clear dates ready to be repopulated.
     */
    clearDates(tasks) {
        for (const task of tasks) {
            this.clearTaskDates(task);
            // Clear the critical flag to force it to be recalculated
            task.critical = null;
        }
    }
    clearTaskDates(task) {
        task.start = null;
        task.finish = null;
        task.early_start = null;
        task.early_finish = null;
        task.late_start = null;
        task.late_finish = null;
        task.remaining_early_start = null;
        task.remaining_early_finish = null;
        task.remaining_late_start = null;
        task.remaining_late_finish = null;
    }
    useTaskEffectiveCalendar(task) {
        return (task.type != ActivityType.RESOURCE_DEPENDENT ||
            !this.getResourcesForTask(task).find((r) => r.type == MpxjResourceType.WORK));
    }
    getEffectiveCalendar(task) {
        if (task.calendar_unique_id === this.m_twentyFourHourCalendar.uniqueId) {
            return this.m_twentyFourHourCalendar;
        }
        if (task.calendar_unique_id != null) {
            return this.m_calendars.find(c => c.uniqueId === task.calendar_unique_id) ?? this.getDefaultCalendar();
        }
        return this.getDefaultCalendar();
    }
    getDefaultCalendar() {
        return this.m_calendars.find(c => c.uniqueId === this.m_propertyValues.default_calendar_unique_id) || Calendar.getDefaultCalendar();
    }
    getDateFromStartAndDuration(task, date) {
        return this.getEffectiveCalendar(task).getDateFromPositiveDuration(this.requireDate(date), task.duration ?? 0);
    }
    getDateFromFinishAndDuration(task, date) {
        return this.getEffectiveCalendar(task).getDateFromNegativeDuration(this.requireDate(date), -(task.duration ?? 0));
    }
    getNextWorkStart(task, date) {
        return this.getEffectiveCalendar(task).getNextWorkStart(this.requireDate(date));
    }
    /**
     * Calculate a date from a finish date minus the task remaining duration.
     *
     * @param task parent task
     * @param date finish date
     * @return finish date minus task remaining duration
     */
    getDateFromFinishAndRemainingDuration(task, date) {
        const remaining = task.remaining_duration ?? 0;
        const result = this.getEffectiveCalendar(task).getDate(this.requireDate(date), -remaining);
        return result;
    }
    getDateFromStartAndRemainingDuration(task, date) {
        return this.getEffectiveCalendar(task).getDate(this.requireDate(date), task.remaining_duration ?? 0);
    }
    /**
     * Get the maximum early start date from all predecessor relationships.
     *
     * @param task task to get predecessors from
     * @return maximum early start date
     * @throws CpmException if no valid early start dates found
     */
    getMaxPredecessorEarlyStart(predecessors, currentTask) {
        const earlyStartDates = predecessors
            .map((rel) => {
            return this.calculateEarlyStart(rel, currentTask);
        })
            .filter((date) => date != null);
        if (earlyStartDates.length === 0) {
            const id = currentTask?.unique_id ?? predecessors[0]?.successor_task_unique_id;
            throw new CpmException(`Missing early start date for task ${id ?? '?'}`);
        }
        return new Date(Math.max(...earlyStartDates.map((d) => d.getTime())));
    }
    hasActualDuration(task) {
        return task.actual_duration != null && task.actual_duration > 0;
    }
    hasRemainingDuration(task) {
        return task.remaining_duration != null && task.remaining_duration > 0;
    }
    getTask(taskUniqueId) {
        if (taskUniqueId == null)
            return null;
        return (this.m_tasks || []).find((t) => t.unique_id === taskUniqueId) ?? null;
    }
    filterPredecessors(relations) {
        return (relations || []).filter((rel) => {
            const task = this.getTask(rel.predecessor_task_unique_id || rel.task_unique_id);
            return task != null && isActivity(task);
        });
    }
    filterSuccessors(relations) {
        return (relations || []).filter((rel) => {
            const task = this.getTask(rel.successor_task_unique_id || rel.task_unique_id);
            return task != null && isActivity(task);
        });
    }
    /** Early finish already computed this pass, else stored finish dates. */
    predecessorEarlyFinish(predecessorTask) {
        return (DateUtils.parseISO(predecessorTask.early_finish) ??
            DateUtils.parseISO(predecessorTask.finish) ??
            DateUtils.parseISO(predecessorTask.planned_finish) ??
            DateUtils.parseISO(predecessorTask.actual_finish));
    }
    /** Early start already computed this pass, else stored start dates. */
    predecessorEarlyStart(predecessorTask) {
        return (DateUtils.parseISO(predecessorTask.early_start) ??
            DateUtils.parseISO(predecessorTask.start) ??
            DateUtils.parseISO(predecessorTask.planned_start) ??
            DateUtils.parseISO(predecessorTask.actual_start));
    }
    /**
     * Perform the CPM forward pass.
     *
     * @param tasks tasks in order for forward pass
     */
    forwardPass(tasks) {
        tasks.forEach((task) => this.forwardPassTask(task));
    }
    /**
     * Perform the CPM forward pass for this task.
     *
     * @param task task to schedule
     */
    forwardPassTask(task) {
        var earlyStart = null;
        var earlyFinish = null;
        const predecessors = this.filterPredecessors(task.predecessors);
        if (task.actual_start == null) {
            if (predecessors.length === 0) {
                switch (task.constraint_type) {
                    case ConstraintType.START_NO_EARLIER_THAN:
                        earlyStart = DateUtils.parseISO(task.constraint_date);
                        if (earlyStart && earlyStart < this.m_dataDate) {
                            earlyStart = this.m_dataDate;
                        }
                        break;
                    case ConstraintType.FINISH_NO_EARLIER_THAN:
                        earlyFinish = DateUtils.parseISO(task.constraint_date);
                        earlyStart = this.getDateFromFinishAndDuration(task, earlyFinish);
                        break;
                    default:
                        earlyStart = this.m_projectStartDate;
                        break;
                }
            }
            else {
                earlyStart = this.getMaxPredecessorEarlyStart(predecessors, task);
            }
            let constraintDate = DateUtils.parseISO(task.constraint_date);
            switch (task.constraint_type) {
                case ConstraintType.START_NO_EARLIER_THAN:
                    if (earlyStart && task.constraint_date && earlyStart < this.requireIso(task.constraint_date)) {
                        earlyStart = constraintDate;
                    }
                    break;
                case ConstraintType.FINISH_NO_EARLIER_THAN:
                    const earliestStart = this.getDateFromFinishAndDuration(task, constraintDate);
                    if (earlyStart && earlyStart < earliestStart) {
                        earlyStart = earliestStart;
                    }
                    break;
                case ConstraintType.MUST_START_ON:
                    earlyStart = constraintDate;
                    break;
                case ConstraintType.START_ON:
                    if (earlyStart && constraintDate && earlyStart < constraintDate) {
                        earlyStart = this.getNextWorkStart(task, constraintDate);
                    }
                    break;
                case ConstraintType.MUST_FINISH_ON:
                    earlyFinish = constraintDate;
                    earlyStart = this.getDateFromFinishAndDuration(task, earlyFinish);
                    break;
                case ConstraintType.FINISH_ON:
                    const startOn = this.getDateFromFinishAndDuration(task, constraintDate);
                    if (startOn && earlyStart && startOn > earlyStart) {
                        earlyFinish = constraintDate;
                        earlyStart = startOn;
                    }
                    break;
                default: {
                    break;
                }
            }
            // Don't adjust early start for a finish milestone
            if (task.activity_type != ActivityType.FINISH_MILESTONE) {
                // Next work start
                earlyStart = this.getNextWorkStart(task, earlyStart);
            }
        }
        else {
            if (task.actual_finish == null) {
                if (predecessors.length === 0) {
                    if (!this.hasActualDuration(task)) {
                        earlyStart = this.getNextWorkStart(task, this.m_dataDate);
                        earlyFinish = this.getDateFromStartAndDuration(task, earlyStart);
                    }
                    else {
                        if (this.hasRemainingDuration(task)) {
                            earlyFinish = this.getDateFromStartAndDuration(task, this.requireIso(task.actual_start));
                            // Sometimes this instead... not sure why?
                            //earlyFinish = getNextWorkStart(task, getDateFromStart(task, task.getActualStart()));
                            earlyStart = this.getDateFromFinishAndRemainingDuration(task, earlyFinish);
                        }
                        else {
                            earlyStart = this.getNextWorkStart(task, this.m_dataDate);
                            earlyFinish = earlyStart;
                        }
                    }
                }
                else {
                    const maxEarlyStart = this.getMaxPredecessorEarlyStart(predecessors);
                    earlyStart = this.getNextWorkStart(task, maxEarlyStart);
                    earlyFinish = this.getDateFromStartAndRemainingDuration(task, earlyStart);
                }
            }
            else {
                if (predecessors.length === 0) {
                    earlyStart = this.m_dataDate;
                    earlyFinish = this.m_dataDate;
                }
                else {
                    earlyStart = this.getMaxPredecessorEarlyStart(predecessors, task);
                    earlyFinish = this.getDateFromStartAndRemainingDuration(task, earlyStart);
                }
            }
        }
        if (task.external_early_start != null &&
            this.requireIso(task.external_early_start) > this.requireDate(earlyStart)) {
            earlyStart = this.requireIso(task.external_early_start);
            earlyFinish = null;
        }
        if (earlyFinish == null) {
            earlyFinish =
                task.actual_finish == null
                    ? this.getDateFromStartAndDuration(task, earlyStart)
                    : this.requireIso(task.actual_finish);
        }
        task.early_start = DateUtils.toIso(earlyStart);
        task.early_finish = DateUtils.toIso(earlyFinish);
        this.setRemainingEarlyDates(task);
    }
    /**
     * Populate the remaining early dates.
     *
     * @param task target task
     */
    setRemainingEarlyDates(task) {
        if (task.actual_finish != null) {
            return;
        }
        var remainingEarlyStart;
        if (task.actual_start == null) {
            remainingEarlyStart = this.requireIso(task.early_start);
        }
        else {
            if (task.actual_duration != null && task.actual_duration != 0) {
                remainingEarlyStart = this.requireIso(task.early_start);
            }
            else {
                if (task.actual_duration == null ||
                    task.actual_duration == 0 ||
                    this.requireIso(task.actual_start) > this.m_dataDate) {
                    remainingEarlyStart = this.requireIso(task.early_start);
                }
                else {
                    remainingEarlyStart = this.getNextWorkStart(task, this.m_dataDate);
                }
            }
        }
        const remainingEarlyFinish = this.getDateFromStartAndRemainingDuration(task, remainingEarlyStart);
        task.remaining_early_start = DateUtils.toIso(remainingEarlyStart);
        task.remaining_early_finish = DateUtils.toIso(remainingEarlyFinish);
    }
    /**
     * Calculate the early start for the successor task in this relationship.
     *
     * @param relation relationship between two tasks
     * @param currentTask the current task being processed (successor)
     * @return calculated early start date
     */
    calculateEarlyStart(relation, currentTask) {
        switch (relation.type) {
            case RelationshipType.FINISH_START:
                return this.calculateEarlyStartForFinishStart(relation, currentTask);
            case RelationshipType.START_START:
                return this.calculateEarlyStartForStartStart(relation, currentTask);
            case RelationshipType.FINISH_FINISH:
                return this.calculateEarlyStartForFinishFinish(relation, currentTask);
            case RelationshipType.START_FINISH:
                return this.calculateEarlyStartForStartFinish(relation, currentTask);
            default:
                throw new CpmException('Unsupported relationship type: ' + relation.type);
        }
    }
    calculateEarlyStartForFinishStart(relation, currentTask) {
        const predecessorTask = this.getPredecessorTask(relation);
        const successorTask = this.getSuccessorTask(relation, currentTask);
        const earlyFinish = this.predecessorEarlyFinish(predecessorTask);
        if (!earlyFinish) {
            return null;
        }
        if (!predecessorTask.actual_start) {
            // Predecessor not started
            if (!successorTask.actual_start) {
                // Successor not started
                if (relation.lag == null || relation.lag == 0) {
                    return earlyFinish;
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.addLag(relation, earlyFinish);
                }
                return this.addLag(relation, earlyFinish);
            }
            // successor started
            if (!successorTask.actual_finish) {
                // successor not finished
                if (relation.lag == 0) {
                    return this.addLag(relation, earlyFinish);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.addLag(relation, earlyFinish);
                }
                return this.addLag(relation, earlyFinish);
            }
            // successor finished
            if (relation.lag == 0) {
                return this.addLag(relation, earlyFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.addLag(relation, earlyFinish);
            }
            return this.addLag(relation, earlyFinish);
        }
        // Predecessor started
        if (predecessorTask.actual_finish) {
            const actualFinish = this.requireIso(predecessorTask.actual_finish);
            // Predecessor finished
            if (!successorTask.actual_start) {
                // Successor not started
                if (relation.lag == 0) {
                    return earlyFinish;
                }
                if ((relation.lag ?? 0) > 0) {
                    const actualLagDurationInHours = actualFinish > this.m_dataDate
                        ? 0
                        : (this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate) ?? 0) /
                            DateUtils.SECONDS_PER_HOUR;
                    const lagDurationInHours = (relation.lag ?? 0) / DateUtils.SECONDS_PER_HOUR;
                    if (lagDurationInHours > actualLagDurationInHours) {
                        const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                        return this.addLag_i(relation, earlyFinish, remainingLag);
                    }
                    return earlyFinish;
                }
                return earlyFinish;
            }
            // successor started
            if (!successorTask.actual_finish) {
                // successor not finished
                if (relation.lag == 0) {
                    return earlyFinish;
                }
                if ((relation.lag ?? 0) > 0) {
                    return earlyFinish;
                }
                return earlyFinish;
            }
            // successor finished
            if (relation.lag == 0) {
                return earlyFinish;
            }
            if ((relation.lag ?? 0) > 0) {
                return earlyFinish;
            }
            return earlyFinish;
        }
        // Predecessor not finished
        if (!successorTask.actual_start) {
            // Successor not started
            if (relation.lag == 0) {
                return this.getNextWorkStart(successorTask, earlyFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.addLag(relation, earlyFinish);
            }
            return this.addLag(relation, earlyFinish);
        }
        // successor started
        if (!successorTask.actual_finish) {
            // successor not finished
            if (relation.lag == 0) {
                return this.addLag(relation, earlyFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.addLag(relation, earlyFinish);
            }
            return this.addLag(relation, earlyFinish);
        }
        // successor finished
        if (relation.lag == 0) {
            return this.addLag(relation, earlyFinish);
        }
        if ((relation.lag ?? 0) > 0) {
            return this.addLag(relation, earlyFinish);
        }
        return this.addLag(relation, earlyFinish);
    }
    getPredecessorTask(relation) {
        const id = relation.predecessor_task_unique_id ?? relation.task_unique_id;
        const task = id != null ? (this.m_tasks || []).find((t) => t.unique_id === id) : undefined;
        if (!task) {
            throw new CpmException(`Missing predecessor task for relation`);
        }
        return task;
    }
    getSuccessorTask(relation, currentTask) {
        // If successor_task_unique_id is not set, use the current task being processed
        if (!relation.successor_task_unique_id) {
            if (!currentTask) {
                throw new CpmException(`No successor task specified in relation from task ${relation.predecessor_task_unique_id} `);
            }
            return currentTask;
        }
        const task = (this.m_tasks || []).find((t) => t.unique_id === relation.successor_task_unique_id);
        if (!task) {
            throw new CpmException(`Successor task not found with unique id ${relation.successor_task_unique_id}`);
        }
        return task;
    }
    addLag(relation, date) {
        return this.addLag_i(relation, date, relation.lag ?? 0);
    }
    /**
     * Add lag to a date.
     *
     * @param relation parent relation
     * @param date date
     * @param lag lag
     * @return date plus lag
     */
    addLag_i(relation, date, lag) {
        const start = this.requireDate(date);
        const result = this.getLagCalendar(relation).getDate(start, lag);
        if (lag < 0 && result < this.m_dataDate) {
            return this.m_dataDate;
        }
        return result;
    }
    /**
     * Retrieve the lag calendar to use when adding/removing lag.
     *
     * @param relation parent relation
     * @return lag calendar
     */
    getLagCalendar(relation) {
        switch (this.m_propertyValues.relationship_lag_calendar) {
            case RelationshipLagCalendar.PREDECESSOR:
                return this.getEffectiveCalendar(this.getPredecessorTask(relation));
            case RelationshipLagCalendar.SUCCESSOR:
                return this.getEffectiveCalendar(this.getSuccessorTask(relation));
            case RelationshipLagCalendar.PROJECT_DEFAULT:
                return this.getDefaultCalendar();
            case RelationshipLagCalendar.TWENTY_FOUR_HOUR:
            default:
                return this.m_twentyFourHourCalendar;
        }
    }
    calculateEarlyStartForStartStart(relation, currentTask) {
        const predecessorTask = this.getPredecessorTask(relation);
        const successorTask = this.getSuccessorTask(relation, currentTask);
        const earlyStart = this.predecessorEarlyStart(predecessorTask);
        if (!earlyStart) {
            return null;
        }
        if (!predecessorTask.actual_start) {
            // Predecessor not started
            if (!successorTask.actual_start) {
                // Successor not started
                if (relation.lag == null || relation.lag == 0) {
                    // why adjust the next work start with the lag calendar? not sure, but it seems to work ;-)
                    return this.getLagCalendar(relation).getNextWorkStart(earlyStart);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.addLag(relation, earlyStart);
                }
                return this.addLag(relation, earlyStart);
            }
            // successor started
            if (!successorTask.actual_finish) {
                // successor not finished
                if (relation.lag == 0) {
                    return this.addLag(relation, earlyStart);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.addLag(relation, earlyStart);
                }
                return this.addLag(relation, earlyStart);
            }
            // successor finished
            if (relation.lag == 0) {
                return this.addLag(relation, earlyStart);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.addLag(relation, earlyStart);
            }
            return this.addLag(relation, earlyStart);
        }
        // Predecessor started
        if (predecessorTask.actual_finish) {
            const actualStart = this.requireIso(predecessorTask.actual_start);
            // Predecessor finished
            if (!successorTask.actual_start) {
                // Successor not started
                if (relation.lag == 0) {
                    const calculatedEarlyStart = this.addLag(relation, actualStart);
                    if (calculatedEarlyStart < this.m_dataDate) {
                        return earlyStart;
                    }
                    return calculatedEarlyStart;
                }
                if ((relation.lag ?? 0) > 0) {
                    const calculatedEarlyStart = this.addLag(relation, actualStart);
                    if (calculatedEarlyStart < this.m_dataDate) {
                        return earlyStart;
                    }
                    return calculatedEarlyStart;
                }
                return this.addLag(relation, actualStart);
            }
            // successor started
            if (!successorTask.actual_finish) {
                // successor not finished
                if (relation.lag == 0) {
                    return earlyStart;
                }
                if ((relation.lag ?? 0) > 0) {
                    const calculatedEarlyStart = this.addLag(relation, actualStart);
                    if (calculatedEarlyStart < this.m_dataDate) {
                        return earlyStart;
                    }
                    return calculatedEarlyStart;
                }
                return earlyStart;
            }
            // successor finished
            if (relation.lag == 0) {
                return earlyStart;
            }
            if ((relation.lag ?? 0) > 0) {
                return earlyStart;
            }
            return earlyStart;
        }
        // Predecessor not finished
        if (!successorTask.actual_start) {
            // Successor not started
            const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
            const actualDurationInHours = (predecessorTask.actual_duration || 0) / DateUtils.SECONDS_PER_HOUR;
            if (actualDurationInHours == 0 || lagDurationInHours <= 0.0) {
                // We have a milestone, or we have no positive lag
                return earlyStart;
            }
            if (actualDurationInHours >= lagDurationInHours) {
                // We have progressed more than the lag
                return earlyStart;
            }
            // We still need to account for some or all of the lag
            const remainingLag = (lagDurationInHours - actualDurationInHours) * DateUtils.SECONDS_PER_HOUR;
            return this.addLag_i(relation, earlyStart, remainingLag);
        }
        // successor started
        if (!successorTask.actual_finish) {
            // successor not finished
            const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
            if (lagDurationInHours <= 0.0) {
                // We have no positive lag
                return earlyStart;
            }
            const actualDurationInHours = (predecessorTask.actual_duration || 0) / DateUtils.SECONDS_PER_HOUR;
            if (actualDurationInHours >= lagDurationInHours) {
                // We have progressed more than the lag
                return earlyStart;
            }
            // We still need to account for some or all of the lag
            const remainingLag = (lagDurationInHours - actualDurationInHours) * DateUtils.SECONDS_PER_HOUR;
            return this.addLag_i(relation, earlyStart, remainingLag);
        }
        // successor finished
        if (relation.lag == 0) {
            return earlyStart;
        }
        if ((relation.lag ?? 0) > 0) {
            return earlyStart;
        }
        return earlyStart;
    }
    calculateEarlyStartForFinishFinish(relation, currentTask) {
        const predecessorTask = this.getPredecessorTask(relation);
        const successorTask = this.getSuccessorTask(relation, currentTask);
        const earlyFinish = this.predecessorEarlyFinish(predecessorTask);
        if (!earlyFinish) {
            return null;
        }
        if (!predecessorTask.actual_start) {
            // Predecessor not started
            if (!successorTask.actual_start) {
                // Successor not started
                if (relation.lag == null || relation.lag == 0) {
                    return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
                }
                return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
            }
            // successor started
            if (!successorTask.actual_finish) {
                // successor not finished
                if (relation.lag == 0) {
                    return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
                }
                return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
            }
            // successor finished
            if (relation.lag == 0) {
                return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
            }
            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
        }
        // Predecessor started
        if (predecessorTask.actual_finish) {
            const actualFinish = this.requireIso(predecessorTask.actual_finish);
            // Predecessor finished
            if (!successorTask.actual_start) {
                // Successor not started
                if (relation.lag == 0) {
                    return this.getDateFromFinishAndDuration(successorTask, actualFinish);
                }
                if ((relation.lag ?? 0) > 0) {
                    const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
                    const actualDurationInHours = (predecessorTask.actual_duration || 0) / DateUtils.SECONDS_PER_HOUR;
                    if (lagDurationInHours > actualDurationInHours) {
                        return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, actualFinish));
                    }
                    return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, this.addLag(relation, earlyFinish)));
                }
                return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, this.addLag(relation, earlyFinish)));
            }
            // successor started
            if (!successorTask.actual_finish) {
                // successor not finished
                if (relation.lag == 0) {
                    return this.getDateFromFinishAndDuration(successorTask, actualFinish);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, actualFinish));
                }
                return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, actualFinish));
            }
            // successor finished
            if (relation.lag == 0) {
                return this.m_dataDate;
                // but sometimes it is
                // this.getLagCalendar(relation).getNextWorkStart(this.m_dataDate)
                // why? remaining lag maybe?
            }
            if ((relation.lag ?? 0) > 0) {
                return this.m_dataDate;
                // but sometimes it is
                // this.getLagCalendar(relation).getNextWorkStart(this.m_dataDate)
                // why? remaining lag maybe?
            }
            return this.m_dataDate;
            // but sometimes it is
            // this.getLagCalendar(relation).getNextWorkStart(this.m_dataDate)
            // why? remaining lag maybe?
        }
        // Predecessor not finished
        if (!successorTask.actual_start) {
            // Successor not started
            if (relation.lag == 0) {
                return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
            }
            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
        }
        // successor started
        if (!successorTask.actual_finish) {
            // successor not finished
            if (relation.lag == 0) {
                return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
            }
            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
        }
        // successor finished
        if (relation.lag == 0) {
            return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
        }
        if ((relation.lag ?? 0) > 0) {
            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
        }
        return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
    }
    calculateEarlyStartForStartFinish(relation, currentTask) {
        const predecessorTask = this.getPredecessorTask(relation);
        const successorTask = this.getSuccessorTask(relation, currentTask);
        const earlyStartDate = this.predecessorEarlyStart(predecessorTask);
        if (!earlyStartDate) {
            return null;
        }
        let earlyStart;
        if (!predecessorTask.actual_start) {
            // Predecessor not started
            if (!successorTask.actual_start) {
                // Successor not started
                if (relation.lag == null || relation.lag == 0) {
                    earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
                }
                else {
                    earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
                }
            }
            else {
                // successor started
                if (!successorTask.actual_finish) {
                    // successor not finished
                    const actualStart = this.requireIso(successorTask.actual_start);
                    if (relation.lag == null || relation.lag <= 0) {
                        const earlyFinish = this.getDateFromStartAndDuration(successorTask, actualStart);
                        earlyStart = this.getDateFromFinishAndRemainingDuration(successorTask, earlyFinish);
                    }
                    else {
                        const earlyFinish = this.addLag(relation, earlyStartDate);
                        earlyStart = this.getDateFromFinishAndRemainingDuration(successorTask, earlyFinish);
                    }
                }
                else {
                    // successor finished
                    if (relation.lag == 0) {
                        earlyStart = earlyStartDate;
                    }
                    else {
                        earlyStart = this.addLag(relation, earlyStartDate);
                    }
                }
            }
        }
        else {
            // Predecessor started
            if (predecessorTask.actual_finish) {
                // Predecessor finished
                if (!successorTask.actual_start) {
                    // Successor not started
                    if (relation.lag == 0) {
                        earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
                    }
                    else {
                        earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
                    }
                }
                else {
                    // successor started
                    if (!successorTask.actual_finish) {
                        // successor not finished
                        const actualStart = this.requireIso(predecessorTask.actual_start);
                        const actualLagDurationInHours = actualStart > this.m_dataDate
                            ? 0
                            : (this.getLagCalendar(relation).getWork(actualStart, this.m_dataDate) || 0) /
                                DateUtils.SECONDS_PER_HOUR;
                        const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
                        if (lagDurationInHours > actualLagDurationInHours) {
                            const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                            const earlyFinish = this.addLag_i(relation, earlyStartDate, remainingLag);
                            earlyStart = this.getDateFromFinishAndRemainingDuration(successorTask, earlyFinish);
                        }
                        else {
                            earlyStart = earlyStartDate;
                        }
                    }
                    else {
                        // successor finished
                        if (relation.lag == 0) {
                            earlyStart = earlyStartDate;
                        }
                        else {
                            earlyStart = this.addLag(relation, earlyStartDate);
                            if (earlyStart > this.m_dataDate) {
                                earlyStart = this.m_dataDate;
                            }
                        }
                    }
                }
            }
            else {
                // Predecessor not finished
                if (!successorTask.actual_start) {
                    // Successor not started
                    if (relation.lag == 0) {
                        earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
                        if (earlyStart > this.m_dataDate) {
                            earlyStart = this.m_dataDate;
                        }
                    }
                    else {
                        earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
                        if (earlyStart > this.m_dataDate) {
                            earlyStart = this.m_dataDate;
                        }
                    }
                }
                else {
                    // successor started
                    if (!successorTask.actual_finish) {
                        // successor not finished
                        const actualStart = this.requireIso(predecessorTask.actual_start);
                        const actualLagDurationInHours = actualStart > this.m_dataDate
                            ? 0
                            : (this.getLagCalendar(relation).getWork(actualStart, this.m_dataDate) || 0) /
                                DateUtils.SECONDS_PER_HOUR;
                        const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
                        if (lagDurationInHours > actualLagDurationInHours) {
                            const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                            const earlyFinish = this.addLag_i(relation, earlyStartDate, remainingLag);
                            earlyStart = this.getDateFromFinishAndRemainingDuration(successorTask, earlyFinish);
                        }
                        else {
                            earlyStart = earlyStartDate;
                        }
                    }
                    else {
                        if (relation.lag == 0) {
                            // successor finished
                            earlyStart = earlyStartDate;
                        }
                        else {
                            // successor finished
                            earlyStart = this.getNextWorkStart(successorTask, this.m_dataDate);
                        }
                    }
                }
            }
        }
        return earlyStart;
    }
    getMinSuccessorLateFinish(successors) {
        const lateFinishDates = successors.map((rel) => {
            const date = this.calculateLateFinish(rel);
            return date;
        }).filter((date) => date != null);
        if (lateFinishDates.length === 0) {
            throw new CpmException('Missing early start date');
        }
        return new Date(Math.min(...lateFinishDates.map((d) => d.getTime())));
        ;
    }
    getEquivalentPreviousWorkFinish(task, date) {
        const d = this.requireDate(date);
        const calendar = this.getEffectiveCalendar(task);
        const previousWorkFinish = calendar.getPreviousWorkFinish(d);
        if (calendar.getWork(previousWorkFinish, d) == 0) {
            return previousWorkFinish;
        }
        return d;
    }
    calculateLateFinish(relation) {
        switch (relation.type) {
            case RelationshipType.START_START:
                return this.adjustLateFinish(relation, this.calculateLateFinishForStartStart(relation));
            case RelationshipType.FINISH_FINISH:
                return this.adjustLateFinish(relation, this.calculateLateFinishForFinishFinish(relation));
            case RelationshipType.START_FINISH:
                return this.adjustLateFinish(relation, this.calculateLateFinishForStartFinish(relation));
            case RelationshipType.FINISH_START:
                return this.adjustLateFinish(relation, this.calculateLateStartForFinishStart(relation));
            default:
                throw new CpmException('Unsupported relationship type: ' + relation.type);
        }
    }
    /**
     * Calculate the late finish for the predecessor task in a start-start relationship.
     *
     * @param relation relationship between two tasks
     * @return calculated late finish date
     */
    calculateLateFinishForStartStart(relation) {
        const predecessorTask = this.getPredecessorTask(relation);
        const successorTask = this.getSuccessorTask(relation);
        const successorLateStart = this.requireIso(successorTask.late_start);
        const successorLateFinish = this.requireIso(successorTask.late_finish);
        let lateFinish = null;
        let lateStart;
        if (predecessorTask.actual_start == null) {
            // Predecessor not started
            if (successorTask.actual_start == null) {
                // Successor not started
                if (relation.lag == 0) {
                    lateStart = this.getNextWorkStart(predecessorTask, successorLateStart);
                    // Sometimes this - why?
                    //lateStart = predecessorTask.getEffectiveCalendar().getPreviousWorkFinish(successorTask.getLateStart());
                    lateFinish = this.getDateFromStartAndRemainingDuration(predecessorTask, lateStart);
                    // Hmmm... dubious logic. Does this just work for indefensible-tedium or is this general?
                    if ((successorTask.successors == null || successorTask.successors.length == 0) &&
                        successorLateFinish < lateFinish) {
                        lateFinish = successorLateFinish;
                    }
                }
                else {
                    if ((relation.lag ?? 0) > 0) {
                        lateStart = this.getNextWorkStart(predecessorTask, this.removeLag(relation, successorLateStart));
                        lateFinish = this.getDateFromStartAndRemainingDuration(predecessorTask, lateStart);
                    }
                    else {
                        lateStart = this.getNextWorkStart(predecessorTask, this.removeLag(relation, successorLateStart));
                        lateFinish = this.getDateFromStartAndRemainingDuration(predecessorTask, lateStart);
                    }
                }
            }
            else {
                // successor started
                if (successorTask.actual_finish == null) {
                    // successor not finished
                    if (relation.lag == 0) {
                        lateStart = this.removeLag(relation, successorLateStart);
                    }
                    else {
                        if ((relation.lag ?? 0) > 0) {
                            lateStart = this.removeLag(relation, successorLateStart);
                        }
                        else {
                            lateStart = this.removeLag(relation, successorLateStart);
                        }
                    }
                }
                else {
                    // successor finished
                    if (relation.lag == 0) {
                        lateStart = this.removeLag(relation, successorLateStart);
                    }
                    else {
                        if ((relation.lag ?? 0) > 0) {
                            lateStart = this.removeLag(relation, successorLateStart);
                        }
                        else {
                            lateStart = this.removeLag(relation, successorLateStart);
                        }
                    }
                }
            }
        }
        else {
            const predecessorActualStart = this.requireIso(predecessorTask.actual_start);
            // Predecessor Started
            if (predecessorTask.actual_finish != null) {
                // Predecessor finished
                if (successorTask.actual_start == null) {
                    // Successor not started
                    if (relation.lag == 0) {
                        lateStart = successorLateStart;
                    }
                    else {
                        if ((relation.lag ?? 0) > 0) {
                            const actualLagDurationInHours = predecessorActualStart > this.m_dataDate
                                ? 0
                                : (this.getLagCalendar(relation).getWork(predecessorActualStart, this.m_dataDate) ?? 0) /
                                    DateUtils.SECONDS_PER_HOUR;
                            const lagDurationInHours = (relation.lag ?? 0) / DateUtils.SECONDS_PER_HOUR;
                            if (lagDurationInHours > actualLagDurationInHours) {
                                const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                                lateStart = this.removeLag_x(relation, successorLateStart, remainingLag);
                            }
                            else {
                                lateStart = successorLateStart;
                            }
                        }
                        else {
                            lateStart = successorLateStart;
                        }
                    }
                }
                else {
                    // successor started
                    if (successorTask.actual_finish == null) {
                        // successor not finished
                        if (relation.lag == 0) {
                            lateStart = successorLateStart;
                        }
                        else {
                            if ((relation.lag ?? 0) > 0) {
                                lateStart = successorLateStart;
                            }
                            else {
                                lateStart = successorLateStart;
                            }
                        }
                    }
                    else {
                        // successor finished
                        if (relation.lag == 0) {
                            lateStart = successorLateStart;
                        }
                        else {
                            if ((relation.lag ?? 0) > 0) {
                                const actualLagDurationInHours = predecessorActualStart > this.m_dataDate
                                    ? 0
                                    : (this.getLagCalendar(relation).getWork(predecessorActualStart, this.m_dataDate) ?? 0) /
                                        DateUtils.SECONDS_PER_HOUR;
                                const lagDurationInHours = (relation.lag ?? 0) / DateUtils.SECONDS_PER_HOUR;
                                if (lagDurationInHours > actualLagDurationInHours) {
                                    const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                                    lateStart = this.removeLag_x(relation, successorLateStart, remainingLag);
                                }
                                else {
                                    lateStart = successorLateStart;
                                }
                            }
                            else {
                                lateStart = successorLateStart;
                            }
                        }
                    }
                }
            }
            else {
                // Predecessor not finished
                if (successorTask.actual_start == null) {
                    // Successor not started
                    if (relation.lag == 0) {
                        lateStart = successorLateStart;
                    }
                    else {
                        if ((relation.lag ?? 0) > 0) {
                            const actualDurationInHours = (predecessorTask.actual_duration ?? 0) / DateUtils.SECONDS_PER_HOUR;
                            const lagDurationInHours = (relation.lag ?? 0) / DateUtils.SECONDS_PER_HOUR;
                            if (actualDurationInHours >= lagDurationInHours) {
                                lateStart = successorLateStart;
                            }
                            else {
                                const remainingLag = (lagDurationInHours - actualDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                                lateStart = this.removeLag_x(relation, successorLateStart, remainingLag);
                            }
                        }
                        else {
                            lateStart = successorLateStart;
                        }
                    }
                }
                else {
                    // successor started
                    if (successorTask.actual_finish == null) {
                        // successor not finished
                        if (relation.lag == 0) {
                            lateStart = successorLateStart;
                        }
                        else {
                            if ((relation.lag ?? 0) > 0) {
                                const actualDurationInHours = (predecessorTask.actual_duration ?? 0) / DateUtils.SECONDS_PER_HOUR;
                                const lagDurationInHours = (relation.lag ?? 0) / DateUtils.SECONDS_PER_HOUR;
                                if (actualDurationInHours >= lagDurationInHours) {
                                    lateStart = successorLateStart;
                                }
                                else {
                                    const remainingLag = (lagDurationInHours - actualDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                                    lateStart = this.removeLag_x(relation, successorLateStart, remainingLag);
                                }
                            }
                            else {
                                lateStart = successorLateStart;
                            }
                        }
                    }
                    else {
                        // successor finished
                        if (relation.lag == 0) {
                            lateStart = successorLateStart;
                        }
                        else {
                            if ((relation.lag ?? 0) > 0) {
                                lateStart = successorLateStart;
                            }
                            else {
                                lateStart = successorLateStart;
                            }
                        }
                    }
                }
            }
        }
        if (lateFinish == null) {
            lateFinish = this.getDateFromStartAndRemainingDuration(predecessorTask, lateStart);
        }
        return lateFinish;
    }
    calculateLateFinishForFinishFinish(relation) {
        const predecessorTask = this.getPredecessorTask(relation);
        const successorTask = this.getSuccessorTask(relation);
        const successorLateFinish = this.requireIso(successorTask.late_finish);
        if (predecessorTask.actual_start == null) {
            // Predecessor not started
            if (successorTask.actual_start == null) {
                // Successor not started
                if (relation.lag == null || relation.lag == 0) {
                    return this.removeLag(relation, successorLateFinish);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.removeLag(relation, successorLateFinish);
                }
                return this.removeLag(relation, successorLateFinish);
            }
            // successor started
            if (successorTask.actual_finish == null) {
                // successor not finished
                if (relation.lag == 0) {
                    return this.removeLag(relation, successorLateFinish);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.removeLag(relation, successorLateFinish);
                }
                return this.removeLag(relation, successorLateFinish);
            }
            // successor finished
            if (relation.lag == 0) {
                return this.removeLag(relation, successorLateFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.removeLag(relation, successorLateFinish);
            }
            return this.removeLag(relation, successorLateFinish);
        }
        // Predecessor Started
        if (predecessorTask.actual_finish != null) {
            const actualFinish = this.requireIso(predecessorTask.actual_finish);
            // Predecessor finished
            if (successorTask.actual_start == null) {
                if (relation.lag == 0) {
                    return successorLateFinish;
                }
                if ((relation.lag ?? 0) > 0) {
                    // Successor not started
                    const actualLagDurationInHours = actualFinish > this.m_dataDate
                        ? 0
                        : (this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate) || 0) /
                            DateUtils.SECONDS_PER_HOUR;
                    const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
                    if (lagDurationInHours > actualLagDurationInHours) {
                        const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                        return this.removeLag_x(relation, successorLateFinish, remainingLag);
                    }
                    return successorLateFinish;
                }
                return successorLateFinish;
            }
            // successor started
            if (successorTask.actual_finish == null) {
                // successor not finished
                if (relation.lag == 0) {
                    return successorLateFinish;
                }
                if ((relation.lag ?? 0) > 0) {
                    return successorLateFinish;
                }
                return successorLateFinish;
            }
            // successor finished
            if (relation.lag == 0) {
                return successorLateFinish;
            }
            if ((relation.lag ?? 0) > 0) {
                const actualLagDurationInHours = actualFinish > this.m_dataDate
                    ? 0
                    : (this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
                const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
                if (lagDurationInHours > actualLagDurationInHours) {
                    const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                    return this.removeLag_x(relation, successorLateFinish, remainingLag);
                }
                return successorLateFinish;
            }
            return successorLateFinish;
        }
        // Predecessor not finished
        if (successorTask.actual_start == null) {
            // Successor not started
            if (relation.lag == 0) {
                return this.removeLag(relation, successorLateFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.removeLag(relation, successorLateFinish);
            }
            return this.removeLag(relation, successorLateFinish);
        }
        // successor started
        if (successorTask.actual_finish == null) {
            // successor not finished
            if (relation.lag == 0) {
                return this.removeLag(relation, successorLateFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.removeLag(relation, successorLateFinish);
            }
            return this.removeLag(relation, successorLateFinish);
        }
        // successor finished
        if (relation.lag == 0) {
            return this.removeLag(relation, successorLateFinish);
        }
        if ((relation.lag ?? 0) > 0) {
            return this.removeLag(relation, successorLateFinish);
        }
        return this.removeLag(relation, successorLateFinish);
    }
    calculateLateFinishForStartFinish(relation) {
        const predecessorTask = this.getPredecessorTask(relation);
        const successorTask = this.getSuccessorTask(relation);
        const successorLateFinish = this.requireIso(successorTask.late_finish);
        if (predecessorTask.actual_start == null) {
            // Predecessor not started
            if (successorTask.actual_start == null) {
                // Successor not started
                if (relation.lag == null || relation.lag == 0) {
                    return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
                }
                return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }
            // successor started
            if (successorTask.actual_finish == null) {
                // successor not finished
                if (relation.lag == 0) {
                    return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
                }
                return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }
            // successor finished
            if (relation.lag == 0) {
                return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }
            if ((relation.lag ?? 0) > 0) {
                return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }
            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
        }
        // Predecessor Started
        if (predecessorTask.actual_finish != null) {
            // Predecessor finished
            if (successorTask.actual_start == null) {
                // Successor not started
                if (relation.lag == 0) {
                    return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
                }
                return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }
            // successor started
            if (successorTask.actual_finish == null) {
                // successor not finished
                if (relation.lag == 0) {
                    return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
                }
                return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }
            // successor finished
            if (relation.lag == 0) {
                return successorLateFinish;
            }
            if ((relation.lag ?? 0) > 0) {
                return successorLateFinish;
            }
            return successorLateFinish;
        }
        // Predecessor not finished
        if (successorTask.actual_start == null) {
            // Successor not started
            if (relation.lag == 0) {
                return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
            }
            return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
        }
        // successor started
        if (successorTask.actual_finish == null) {
            // successor not finished
            if (relation.lag == 0) {
                return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }
            if ((relation.lag ?? 0) > 0) {
                return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }
            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
        }
        // successor finished
        if (relation.lag == 0) {
            return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
        }
        if ((relation.lag ?? 0) > 0) {
            const actualStart = this.requireIso(predecessorTask.actual_start);
            const actualLagDurationInHours = actualStart > this.m_dataDate
                ? 0
                : (this.getLagCalendar(relation).getWork(actualStart, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
            const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
            if (lagDurationInHours > actualLagDurationInHours) {
                const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                return this.removeLag_x(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish), remainingLag);
            }
            return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
        }
        return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
    }
    calculateLateStartForFinishStart(relation) {
        const predecessorTask = this.getPredecessorTask(relation);
        const successorTask = this.getSuccessorTask(relation);
        const successorLateStart = this.requireIso(successorTask.late_start);
        if (predecessorTask.actual_start == null) {
            // Predecessor not started
            if (successorTask.actual_start == null) {
                // Successor not started
                if (relation.lag == null || relation.lag == 0) {
                    return this.removeLag(relation, successorLateStart);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.removeLag(relation, successorLateStart);
                }
                return this.removeLag(relation, successorLateStart);
            }
            // successor started
            if (successorTask.actual_finish == null) {
                // successor not finished
                if (relation.lag == 0) {
                    return this.removeLag(relation, successorLateStart);
                }
                if ((relation.lag ?? 0) > 0) {
                    return this.removeLag(relation, successorLateStart);
                }
                return this.removeLag(relation, successorLateStart);
            }
            // successor finished
            if (relation.lag == 0) {
                return successorLateStart;
            }
            if ((relation.lag ?? 0) > 0) {
                return successorLateStart;
            }
            return successorLateStart;
        }
        // Predecessor Started
        if (predecessorTask.actual_finish != null) {
            const actualFinish = this.requireIso(predecessorTask.actual_finish);
            // Predecessor finished
            if (successorTask.actual_start == null) {
                // Successor not started
                if (relation.lag == 0) {
                    return successorLateStart;
                }
                if ((relation.lag ?? 0) > 0) {
                    const actualLagDurationInHours = actualFinish > this.m_dataDate
                        ? 0
                        : (this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate) || 0) /
                            DateUtils.SECONDS_PER_HOUR;
                    const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
                    if (lagDurationInHours > actualLagDurationInHours) {
                        const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                        return this.removeLag_x(relation, successorLateStart, remainingLag);
                    }
                    return successorLateStart;
                }
                return successorLateStart;
            }
            // successor started
            if (successorTask.actual_finish == null) {
                // check for actual progress
                if (relation.lag == 0) {
                    return successorLateStart;
                }
                if ((relation.lag ?? 0) > 0) {
                    if ((successorTask.actual_duration || 0) === 0) {
                        return this.removeLag(relation, successorLateStart);
                    }
                    return successorLateStart;
                }
                if ((successorTask.actual_duration || 0) === 0) {
                    return this.removeLag(relation, successorLateStart);
                }
                return successorLateStart;
            }
            // successor finished
            if (relation.lag == 0) {
                return successorLateStart;
            }
            if ((relation.lag ?? 0) > 0) {
                const actualLagDurationInHours = actualFinish > this.m_dataDate
                    ? 0
                    : (this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
                const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
                if (lagDurationInHours > actualLagDurationInHours) {
                    const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                    return this.removeLag_x(relation, successorLateStart, remainingLag);
                }
                return successorLateStart;
            }
            return successorLateStart;
        }
        // Predecessor not finished
        if (successorTask.actual_start == null) {
            // Successor not started
            if (relation.lag == 0) {
                return this.removeLag(relation, successorLateStart);
            }
            if ((relation.lag ?? 0) > 0) {
                return this.removeLag(relation, successorLateStart);
            }
            return this.removeLag(relation, successorLateStart);
        }
        // successor started
        if (successorTask.actual_finish == null) {
            // check for actual progress
            if (relation.lag == 0) {
                if ((successorTask.actual_duration || 0) === 0) {
                    return this.removeLag(relation, successorLateStart);
                }
                return successorLateStart;
            }
            if ((relation.lag ?? 0) > 0) {
                if ((successorTask.actual_duration || 0) === 0) {
                    return this.removeLag(relation, successorLateStart);
                }
                return successorLateStart;
            }
            if ((successorTask.actual_duration || 0) === 0) {
                return this.removeLag(relation, successorLateStart);
            }
            return successorLateStart;
        }
        // successor finished
        if (relation.lag == 0) {
            return successorLateStart;
        }
        if ((relation.lag ?? 0) > 0) {
            return successorLateStart;
        }
        return successorLateStart;
    }
    /**
     * Ensure a late finish date is before the project finish date, and adjust if required.
     *
     * @param relation parent relation
     * @param lateFinish late finish date
     * @return late finish
     */
    adjustLateFinish(relation, lateFinish) {
        const before = lateFinish;
        if (lateFinish > this.m_projectFinishDate) {
            // If we're between working periods, move back to the last work finish
            lateFinish = this.getEquivalentPreviousWorkFinish(this.getPredecessorTask(relation), this.m_projectFinishDate);
        }
        return lateFinish;
    }
    /**
     * Perform the CPM backward pass.
     *
     * @param forwardPassTasks tasks in order for forward pass
     */
    backwardPass(tasks) {
        const reversedTasks = [...tasks].reverse();
        for (const task of reversedTasks) {
            this.backwardPassTask(task);
        }
    }
    /**
     * Perform the CPM backward pass for this task.
     *
     * @param task task to schedule
     */
    backwardPassTask(task) {
        const successors = this.filterSuccessors(task.successors);
        let lateFinish = null;
        const constraintDate = DateUtils.parseISO(task.constraint_date);
        if (task.actual_finish == null) {
            if (successors.length === 0) {
                if (task.external_late_finish == null) {
                    if (this.m_propertyValues.must_finish_by != null) {
                        lateFinish = DateUtils.parseISO(this.m_propertyValues.must_finish_by);
                    }
                    else {
                        lateFinish = this.m_projectFinishDate;
                    }
                }
                else {
                    lateFinish = DateUtils.parseISO(task.external_late_finish);
                }
            }
            else {
                lateFinish = this.getMinSuccessorLateFinish(successors);
            }
            switch (task.constraint_type) {
                case ConstraintType.START_ON: {
                    if (task.actual_start == null) {
                        const latestFinish = this.getDateFromStartAndDuration(task, constraintDate);
                        if (lateFinish && lateFinish > latestFinish) {
                            lateFinish = latestFinish;
                        }
                    }
                    break;
                }
                case ConstraintType.MUST_START_ON: {
                    if (task.actual_start == null) {
                        lateFinish = this.getDateFromStartAndDuration(task, constraintDate);
                    }
                    break;
                }
                case ConstraintType.MUST_FINISH_ON: {
                    lateFinish = constraintDate;
                    break;
                }
                case ConstraintType.START_NO_LATER_THAN: {
                    const latestFinish = this.getDateFromStartAndDuration(task, constraintDate);
                    if (lateFinish && lateFinish > latestFinish) {
                        lateFinish = latestFinish;
                    }
                    break;
                }
                case ConstraintType.FINISH_ON:
                case ConstraintType.FINISH_NO_LATER_THAN: {
                    if (lateFinish && constraintDate && lateFinish > constraintDate) {
                        lateFinish = constraintDate;
                    }
                    break;
                }
                default: {
                    break;
                }
            }
            if (task.secondary_constraint_type != null) {
                const secondaryConstraintDate = DateUtils.parseISO(task.secondary_constraint_date);
                switch (task.secondary_constraint_type) {
                    case ConstraintType.START_NO_LATER_THAN: {
                        const latestFinish = this.getDateFromStartAndDuration(task, secondaryConstraintDate);
                        if (lateFinish && lateFinish > latestFinish) {
                            lateFinish = latestFinish;
                        }
                        break;
                    }
                    case ConstraintType.FINISH_NO_LATER_THAN: {
                        if (lateFinish && secondaryConstraintDate && lateFinish > secondaryConstraintDate) {
                            lateFinish = secondaryConstraintDate;
                        }
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }
            // If we are at the start of the next period of work, we can move back to the end of the previous period of work
            lateFinish = this.getEquivalentPreviousWorkFinish(task, lateFinish);
        }
        else {
            if (successors.length === 0) {
                if (this.m_propertyValues.must_finish_by != null) {
                    lateFinish = DateUtils.parseISO(this.m_propertyValues.must_finish_by);
                }
                else {
                    lateFinish = this.m_projectFinishDate;
                }
            }
            else {
                lateFinish = this.getMinSuccessorLateFinish(successors);
            }
        }
        const externalLateFinish = DateUtils.parseISO(task.external_late_finish);
        if (externalLateFinish != null && lateFinish != null && externalLateFinish < lateFinish) {
            lateFinish = externalLateFinish;
        }
        let lateStart = this.getDateFromFinishAndRemainingDuration(task, lateFinish);
        if (task.activity_type == ActivityType.START_MILESTONE) {
            lateStart = this.getNextWorkStart(task, lateStart);
        }
        else {
            if (task.activity_type != ActivityType.FINISH_MILESTONE && this.hasRemainingDuration(task)) {
                lateStart = this.getNextWorkStart(task, lateStart);
            }
        }
        task.late_start = DateUtils.toIso(lateStart);
        task.late_finish = DateUtils.toIso(lateFinish);
        this.setRemainingLateDates(task);
        if (task.constraint_type == ConstraintType.AS_LATE_AS_POSSIBLE) {
            this.alapAdjust(task);
        }
    }
    /**
     * Populate the remaining late dates.
     *
     * @param task target task
     */
    setRemainingLateDates(task) {
        if (task.actual_finish != null) {
            return;
        }
        task.remaining_late_start = task.late_start;
        task.remaining_late_finish = task.late_finish;
    }
    /**
     * Adjust Early Start and Early Finish dates for an activity with
     * an "As Late As Possible" constraint.
     *
     * @param task ALAP constrained task
     */
    alapAdjust(task) {
        let earlyStart = null;
        let earlyFinish = null;
        // Filter relations to only include those where the successor is an activity
        // Calculate early start for each relation and find the earliest one
        const activityRelations = (task.successors || [])
            .filter((rel) => {
            const successorTask = this.getSuccessorTask(rel);
            return successorTask != null && isActivity(successorTask);
        })
            .map((rel) => ({ relation: rel, earlyStart: this.getAlapEarlyStart(rel) }))
            .filter((item) => item.earlyStart != null);
        if (activityRelations.length === 0) {
            earlyFinish = this.m_projectFinishDate;
            earlyStart = this.getDateFromFinishAndRemainingDuration(task, earlyFinish);
        }
        else {
            // Find the relation with the earliest early start
            const earliestRelation = activityRelations.reduce((earliest, current) => current.earlyStart < earliest.earlyStart ? current : earliest);
            earlyStart = earliestRelation.earlyStart;
            earlyFinish = this.getDateFromStartAndRemainingDuration(task, earlyStart);
        }
        task.early_start = DateUtils.toIso(earlyStart);
        task.early_finish = DateUtils.toIso(earlyFinish);
        this.setRemainingEarlyDates(task);
    }
    /**
     * Calculate the ALAP Early Start from a relation's successor task.
     *
     * @param relation target relation
     * @return early start date
     */
    getAlapEarlyStart(relation) {
        const predecessorTask = this.getPredecessorTask(relation);
        const successorTask = this.getSuccessorTask(relation);
        const successorEarlyStart = this.requireIso(successorTask.early_start);
        switch (relation.type) {
            case RelationshipType.START_START: {
                return this.removeLag(relation, successorEarlyStart);
            }
            case RelationshipType.FINISH_START: {
                if (predecessorTask.actual_start == null) {
                    // Predecessor not started
                    if (successorTask.actual_start == null) {
                        // Successor not started
                        return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                    }
                    // successor started
                    if (successorTask.actual_finish == null) {
                        // successor not finished
                        return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                    }
                    // successor finished
                    return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                }
                // Predecessor Started
                if (predecessorTask.actual_finish == null) {
                    // Predecessor not finished
                    if (successorTask.actual_start == null) {
                        // Successor not started
                        return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                    }
                    // successor started
                    if (successorTask.actual_finish == null) {
                        // successor not finished
                        return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                    }
                    // successor finished
                    return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                }
                // Predecessor finished
                if (successorTask.actual_start == null) {
                    // Successor not started
                    return this.getEquivalentNextWorkStart(predecessorTask, this.m_dataDate);
                }
                // successor started
                if (successorTask.actual_finish == null) {
                    // successor not finished
                    return this.getEquivalentNextWorkStart(predecessorTask, this.m_dataDate);
                }
                // successor finished
                return this.m_dataDate;
            }
            case RelationshipType.FINISH_FINISH: {
                const earlyFinish = this.removeLag(relation, this.requireIso(successorTask.early_finish));
                return this.getDateFromFinishAndRemainingDuration(predecessorTask, earlyFinish);
            }
            default: {
                // TODO: need example to determine correct behaviour here
                return this.m_projectFinishDate;
            }
        }
    }
    removeLag_x(relation, date, lag) {
        return this.getLagCalendar(relation).getDate(this.requireDate(date), -lag);
    }
    removeLag(relation, date) {
        return this.getLagCalendar(relation).getDate(this.requireDate(date), -(relation.lag ?? 0));
    }
    getEquivalentNextWorkStart(task, date) {
        const d = this.requireDate(date);
        const calendar = this.getEffectiveCalendar(task);
        const adjustedDate = calendar.getNextWorkStart(d);
        if (calendar.getWork(d, adjustedDate) == 0) {
            return adjustedDate;
        }
        return d;
    }
    calendarWorkSeconds(task, startIso, finishIso) {
        const start = DateUtils.parseISO(startIso);
        const finish = DateUtils.parseISO(finishIso);
        if (start == null || finish == null)
            return null;
        return this.getEffectiveCalendar(task).getWork(start, finish);
    }
    levelOfEffortPass() {
        const activities = new DepthFirstGraphSort(this.m_tasks, this.isLevelOfEffortActivity).sort();
        for (const activity of activities) {
            this.levelOfEffortPassTask(activity);
        }
    }
    /** PrimaveraScheduler.levelOfEffortPass — LOE dates are derived from related activities. */
    levelOfEffortPassTask(task) {
        task.actual_start = null;
        task.actual_finish = null;
        let earlyStartFromPredecessor = null;
        let earlyFinishFromPredecessor = null;
        let lateStartFromPredecessor = null;
        let lateFinishFromPredecessor = null;
        for (const relation of task.predecessors || []) {
            const predecessor = this.getTask(relation.predecessor_task_unique_id ?? relation.task_unique_id);
            if (!predecessor)
                continue;
            switch (relation.type) {
                case RelationshipType.START_START: {
                    if (predecessor.actual_start == null) {
                        earlyStartFromPredecessor = this.loeUpdateIfBefore(earlyStartFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.early_start), false));
                        lateStartFromPredecessor = this.loeUpdateIfBefore(lateStartFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.late_start), false));
                    }
                    else {
                        earlyStartFromPredecessor = this.loeUpdateIfBefore(earlyStartFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.actual_start), true));
                        lateStartFromPredecessor = this.loeUpdateIfBefore(lateStartFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.late_start), false));
                    }
                    break;
                }
                case RelationshipType.FINISH_START: {
                    if (predecessor.actual_finish == null) {
                        earlyStartFromPredecessor = this.loeUpdateIfBefore(earlyStartFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.early_finish), false));
                        lateStartFromPredecessor = this.loeUpdateIfBefore(lateStartFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.late_finish), false));
                    }
                    else {
                        const candidateLateStart = predecessor.activity_type === ActivityType.START_MILESTONE ? predecessor.late_start : predecessor.actual_finish;
                        earlyStartFromPredecessor = this.loeUpdateIfBefore(earlyStartFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.actual_finish), true));
                        lateStartFromPredecessor = this.loeUpdateIfBefore(lateStartFromPredecessor, this.loeFrom(this.loeAddLag(relation, candidateLateStart), true));
                    }
                    break;
                }
                case RelationshipType.START_FINISH: {
                    if (predecessor.actual_start == null) {
                        earlyFinishFromPredecessor = this.loeUpdateIfAfter(earlyFinishFromPredecessor, this.loeFrom(this.adjustFinish(task, this.loeAddLag(relation, predecessor.early_start)), false));
                        lateFinishFromPredecessor = this.loeUpdateIfAfter(lateFinishFromPredecessor, this.loeFrom(this.adjustFinish(task, this.loeAddLag(relation, predecessor.late_start)), false));
                    }
                    else {
                        earlyFinishFromPredecessor = this.loeUpdateIfAfter(earlyFinishFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.actual_start), true));
                        lateFinishFromPredecessor = this.loeUpdateIfAfter(lateFinishFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.actual_start), true));
                    }
                    break;
                }
                case RelationshipType.FINISH_FINISH: {
                    if (predecessor.actual_finish == null) {
                        earlyFinishFromPredecessor = this.loeUpdateIfAfter(earlyFinishFromPredecessor, this.loeFrom(this.adjustFinish(task, this.loeAddLag(relation, predecessor.early_finish)), false));
                        lateFinishFromPredecessor = this.loeUpdateIfAfter(lateFinishFromPredecessor, this.loeFrom(this.adjustFinish(task, this.loeAddLag(relation, predecessor.late_finish)), false));
                    }
                    else {
                        earlyFinishFromPredecessor = this.loeUpdateIfAfter(earlyFinishFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.actual_finish), true));
                        lateFinishFromPredecessor = this.loeUpdateIfAfter(lateFinishFromPredecessor, this.loeFrom(this.loeAddLag(relation, predecessor.actual_finish), true));
                    }
                    break;
                }
                default:
                    break;
            }
        }
        let earlyStartFromSuccessor = null;
        let earlyFinishFromSuccessor = null;
        let lateStartFromSuccessor = null;
        let lateFinishFromSuccessor = null;
        for (const relation of task.successors || []) {
            const successor = this.getTask(relation.successor_task_unique_id);
            if (!successor)
                continue;
            switch (relation.type) {
                case RelationshipType.START_START: {
                    if (successor.actual_start == null) {
                        earlyStartFromSuccessor = this.loeUpdateIfBefore(earlyStartFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.early_start), false));
                        lateStartFromSuccessor = this.loeUpdateIfBefore(lateStartFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.late_start), false));
                    }
                    else {
                        earlyStartFromSuccessor = this.loeUpdateIfBefore(earlyStartFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.actual_start), true));
                        lateStartFromSuccessor = this.loeUpdateIfBefore(lateStartFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.actual_start), true));
                    }
                    break;
                }
                case RelationshipType.FINISH_START: {
                    if (successor.actual_start == null) {
                        earlyFinishFromSuccessor = this.loeUpdateIfAfter(earlyFinishFromSuccessor, this.loeFrom(this.adjustFinish(task, this.loeRemoveLag(relation, successor.early_start)), false));
                        lateFinishFromSuccessor = this.loeUpdateIfAfter(lateFinishFromSuccessor, this.loeFrom(this.adjustFinish(task, this.loeRemoveLag(relation, successor.late_start)), false));
                    }
                    else {
                        earlyFinishFromSuccessor = this.loeUpdateIfAfter(earlyFinishFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.actual_start), true));
                        lateFinishFromSuccessor = this.loeUpdateIfAfter(lateFinishFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.actual_start), true));
                    }
                    break;
                }
                case RelationshipType.START_FINISH: {
                    if (successor.actual_finish == null) {
                        earlyStartFromSuccessor = this.loeUpdateIfBefore(earlyStartFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.early_finish), false));
                        lateStartFromSuccessor = this.loeUpdateIfBefore(lateStartFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.late_finish), false));
                    }
                    else {
                        earlyStartFromSuccessor = this.loeUpdateIfBefore(earlyStartFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.actual_finish), true));
                        lateStartFromSuccessor = this.loeUpdateIfBefore(lateStartFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.actual_finish), true));
                    }
                    break;
                }
                case RelationshipType.FINISH_FINISH: {
                    if (successor.actual_finish == null) {
                        earlyFinishFromSuccessor = this.loeUpdateIfAfter(earlyFinishFromSuccessor, this.loeFrom(this.adjustFinish(task, this.loeRemoveLag(relation, successor.early_finish)), false));
                        lateFinishFromSuccessor = this.loeUpdateIfAfter(lateFinishFromSuccessor, this.loeFrom(this.adjustFinish(task, this.loeRemoveLag(relation, successor.late_finish)), false));
                    }
                    else {
                        earlyFinishFromSuccessor = this.loeUpdateIfAfter(earlyFinishFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.actual_finish), true));
                        lateFinishFromSuccessor = this.loeUpdateIfAfter(lateFinishFromSuccessor, this.loeFrom(this.loeRemoveLag(relation, successor.actual_finish), true));
                    }
                    break;
                }
                default:
                    break;
            }
        }
        let earlyStart;
        if (earlyStartFromPredecessor == null && earlyStartFromSuccessor == null) {
            earlyStart = this.loeFrom(this.getNextWorkStart(task, this.m_dataDate), false);
        }
        else if (earlyStartFromPredecessor != null && earlyStartFromSuccessor != null) {
            earlyStart = earlyStartFromPredecessor.value < earlyStartFromSuccessor.value ? earlyStartFromPredecessor : earlyStartFromSuccessor;
        }
        else {
            earlyStart = earlyStartFromPredecessor ?? earlyStartFromSuccessor;
        }
        let earlyFinish;
        if (earlyFinishFromPredecessor == null && earlyFinishFromSuccessor == null) {
            earlyFinish = earlyStart;
        }
        else if (earlyFinishFromPredecessor != null && earlyFinishFromSuccessor != null) {
            earlyFinish = earlyFinishFromSuccessor.value > earlyFinishFromPredecessor.value ? earlyFinishFromSuccessor : earlyFinishFromPredecessor;
        }
        else {
            earlyFinish = earlyFinishFromPredecessor ?? earlyFinishFromSuccessor;
        }
        let lateFinish;
        if (lateFinishFromPredecessor == null && lateFinishFromSuccessor == null) {
            lateFinish = this.loeFrom(this.m_projectFinishDate, false);
        }
        else if (lateFinishFromPredecessor != null && lateFinishFromSuccessor != null) {
            lateFinish = lateFinishFromSuccessor.value > lateFinishFromPredecessor.value ? lateFinishFromSuccessor : lateFinishFromPredecessor;
        }
        else {
            lateFinish = lateFinishFromPredecessor ?? lateFinishFromSuccessor;
        }
        let lateStart;
        if (lateStartFromPredecessor == null && lateStartFromSuccessor == null) {
            lateStart = lateFinish;
        }
        else if (lateStartFromPredecessor != null && lateStartFromSuccessor != null) {
            lateStart = lateStartFromPredecessor.value < lateStartFromSuccessor.value ? lateStartFromPredecessor : lateStartFromSuccessor;
        }
        else {
            lateStart = lateStartFromPredecessor ?? lateStartFromSuccessor;
        }
        if (earlyStart == null || earlyFinish == null || lateStart == null || lateFinish == null) {
            return;
        }
        let start = earlyStart;
        if (earlyStart.value < this.m_dataDate) {
            if (earlyStart.actual) {
                earlyStart = this.loeFrom(this.m_dataDate, true);
            }
            else {
                earlyStart = this.loeFrom(this.getNextWorkStart(task, this.m_dataDate), false);
                start = this.loeFrom(start.value, true);
                task.actual_start = DateUtils.toIso(start.value);
            }
        }
        if (!earlyFinish.actual) {
            if (earlyFinish.value < this.m_dataDate) {
                earlyFinish = this.loeFrom(this.m_dataDate, false);
            }
            if (earlyFinish.value < earlyStart.value) {
                earlyFinish = earlyFinish.actual ? this.loeFrom(earlyStart.value, true) : earlyStart;
            }
        }
        if (lateStart.value > lateFinish.value) {
            lateStart = lateFinish;
        }
        const calendar = this.getEffectiveCalendar(task);
        task.start = DateUtils.toIso(start.actual ? start.value : earlyStart.value);
        task.finish = DateUtils.toIso(earlyFinish.value);
        if (earlyStart.actual) {
            if ((calendar.getWork(this.m_dataDate, this.requireIso(task.start)) ?? 0) <= 0) {
                task.actual_start = task.start;
            }
        }
        if (earlyFinish.actual || lateFinish.actual) {
            if ((calendar.getWork(this.m_dataDate, this.requireIso(task.finish)) ?? 0) <= 0) {
                task.actual_start = task.start;
                task.actual_finish = task.finish;
            }
        }
        task.early_start = DateUtils.toIso(calendar.getNextWorkStart(earlyStart.value));
        task.early_finish = DateUtils.toIso(earlyFinish.value);
        task.late_start = DateUtils.toIso(calendar.getNextWorkStart(lateStart.value));
        task.late_finish = DateUtils.toIso(lateFinish.value);
        if (task.actual_start == null) {
            task.planned_start = task.start;
            task.planned_finish = task.finish;
        }
        if (task.actual_start == null || task.actual_finish == null) {
            task.remaining_early_start = DateUtils.toIso(earlyStart.value);
            task.remaining_early_finish = DateUtils.toIso(earlyFinish.value);
            task.remaining_late_start = DateUtils.toIso(lateStart.value);
            task.remaining_late_finish = DateUtils.toIso(lateFinish.value);
        }
    }
    loeFrom(value, actual) {
        if (value == null)
            return null;
        return { value, actual };
    }
    loeAddLag(relation, iso) {
        const date = DateUtils.parseISO(iso);
        if (date == null)
            return null;
        return this.addLag(relation, date);
    }
    loeRemoveLag(relation, iso) {
        const date = DateUtils.parseISO(iso);
        if (date == null)
            return null;
        return this.removeLag(relation, date);
    }
    loeUpdateIfBefore(current, next) {
        if (current == null)
            return next;
        if (next == null)
            return current;
        return next.value < current.value ? next : current;
    }
    loeUpdateIfAfter(current, next) {
        if (current == null)
            return next;
        if (next == null)
            return current;
        return next.value > current.value ? next : current;
    }
    adjustFinish(task, finish) {
        if (finish == null)
            return null;
        return this.getEquivalentPreviousWorkFinish(task, finish);
    }
    updateDates(tasks) {
        tasks.forEach((task) => this.updateDatesTask(task));
    }
    /**
     * Update Start, Finish, Planned Start, and Planned Finish dates.
     *
     * @param activity activity to update
     */
    updateDatesTask(task) {
        task.start = task.actual_start == null ? task.early_start : task.actual_start;
        task.finish = task.actual_finish == null ? task.early_finish : task.actual_finish;
        // P6 moves the planned start/finish dates for unstarted activities
        if (task.actual_start == null) {
            task.planned_start = task.start;
            task.planned_finish = task.finish;
        }
    }
    /**
     * My method to clear dates for the preview run.
     */
    clearContainerDates(parentTask) {
        const childTasks = this.getChildTasks(parentTask);
        if (childTasks.length == 0) {
            return;
        }
        this.clearTaskDates(parentTask);
        childTasks.forEach((task) => this.clearContainerDates(task));
    }
    /**
     * Roll up dates to a WBS entity.
     *
     * @param parentTask parent WBS
     */
    rollupDatesTask(parentTask) {
        // NOTE: LOE and WBS Summary are ignore at this point as they have null dates
        const childTasks = this.getChildTasks(parentTask);
        if (childTasks.length == 0) {
            return;
        }
        let startDate = parentTask.start;
        let finishDate = parentTask.finish;
        let earlyStartDate = parentTask.early_start;
        let earlyFinishDate = parentTask.early_finish;
        let lateStartDate = parentTask.late_start;
        let lateFinishDate = parentTask.late_finish;
        let remainingEarlyStartDate = parentTask.remaining_early_start;
        let remainingEarlyFinishDate = parentTask.remaining_early_finish;
        let remainingLateStartDate = parentTask.remaining_late_start;
        let remainingLateFinishDate = parentTask.remaining_late_finish;
        let plannedStartDate = parentTask.planned_start;
        let plannedFinishDate = parentTask.planned_finish;
        let actualStartDate = parentTask.actual_start;
        let actualFinishDate = parentTask.actual_finish;
        let baselineStartDate = parentTask.baseline_start;
        let baselineFinishDate = parentTask.baseline_finish;
        let finished = 0;
        let critical = false;
        for (const task of childTasks) {
            // Collect modified tasks from recursive calls
            this.rollupDatesTask(task);
            // the child tasks can have null dates (e.g. for nested wbs elements with no task children) so we
            // still must protect against some children having null dates
            startDate = DateUtils.min(startDate, task.start);
            finishDate = DateUtils.max(finishDate, task.finish);
            plannedStartDate = DateUtils.min(plannedStartDate, task.planned_start);
            plannedFinishDate = DateUtils.max(plannedFinishDate, task.planned_finish);
            actualStartDate = DateUtils.min(actualStartDate, task.actual_start);
            actualFinishDate = DateUtils.max(actualFinishDate, task.actual_finish);
            baselineStartDate = DateUtils.min(baselineStartDate, task.baseline_start);
            baselineFinishDate = DateUtils.max(baselineFinishDate, task.baseline_finish);
            if (task.activity_type != ActivityType.LEVEL_OF_EFFORT || task.actual_finish == null) {
                earlyStartDate = DateUtils.min(earlyStartDate, task.early_start);
                earlyFinishDate = DateUtils.max(earlyFinishDate, task.early_finish);
                remainingEarlyStartDate = DateUtils.min(remainingEarlyStartDate, task.remaining_early_start);
                remainingEarlyFinishDate = DateUtils.max(remainingEarlyFinishDate, task.remaining_early_finish);
                lateStartDate = DateUtils.min(lateStartDate, task.late_start);
                lateFinishDate = DateUtils.max(lateFinishDate, task.late_finish);
                remainingLateStartDate = DateUtils.min(remainingLateStartDate, task.remaining_late_start);
                remainingLateFinishDate = DateUtils.max(remainingLateFinishDate, task.remaining_late_finish);
            }
            if (task.actual_finish != null) {
                ++finished;
            }
            critical = critical || !!task.critical;
        }
        parentTask.start = startDate;
        parentTask.finish = finishDate;
        parentTask.planned_start = plannedStartDate;
        parentTask.planned_finish = plannedFinishDate;
        parentTask.actual_start = actualStartDate;
        parentTask.early_start = earlyStartDate;
        parentTask.early_finish = earlyFinishDate;
        parentTask.remaining_early_start = remainingEarlyStartDate;
        parentTask.remaining_early_finish = remainingEarlyFinishDate;
        parentTask.late_start = lateStartDate;
        parentTask.late_finish = lateFinishDate;
        parentTask.remaining_late_start = remainingLateStartDate;
        parentTask.remaining_late_finish = remainingLateFinishDate;
        parentTask.baseline_start = baselineStartDate;
        parentTask.baseline_finish = baselineFinishDate;
        //
        // Only if all child tasks have actual finish dates do we
        // set the actual finish date on the parent task.
        //
        if (finished == childTasks.length) {
            parentTask.actual_finish = actualFinishDate;
        }
        let plannedDuration = null;
        if (plannedStartDate != null && plannedFinishDate != null) {
            plannedDuration = this.calendarWorkSeconds(parentTask, plannedStartDate, plannedFinishDate);
            parentTask.planned_duration = plannedDuration;
        }
        let actualDuration = null;
        let remainingDuration = null;
        if (parentTask.actual_finish == null) {
            let taskStartDate = parentTask.remaining_early_start;
            if (taskStartDate == null) {
                taskStartDate = parentTask.early_start;
                if (taskStartDate == null) {
                    taskStartDate = plannedStartDate;
                }
            }
            let taskFinishDate = parentTask.remaining_early_finish;
            if (taskFinishDate == null) {
                taskFinishDate = parentTask.early_finish;
                if (taskFinishDate == null) {
                    taskFinishDate = plannedFinishDate;
                }
            }
            if (taskStartDate != null) {
                if (parentTask.actual_start != null) {
                    actualDuration = this.calendarWorkSeconds(parentTask, parentTask.actual_start, taskStartDate);
                }
                if (taskFinishDate != null) {
                    remainingDuration = this.calendarWorkSeconds(parentTask, taskStartDate, taskFinishDate);
                }
            }
        }
        else {
            actualDuration = this.calendarWorkSeconds(parentTask, parentTask.actual_start, parentTask.actual_finish);
            remainingDuration = 0;
        }
        if (actualDuration != null && actualDuration < 0) {
            actualDuration = null;
        }
        if (remainingDuration != null && remainingDuration < 0) {
            remainingDuration = null;
        }
        parentTask.actual_duration = actualDuration;
        parentTask.remaining_duration = remainingDuration;
        parentTask.duration = (actualDuration ?? 0) + (remainingDuration ?? 0);
        if (plannedDuration != null && remainingDuration != null && plannedDuration != 0) {
            let durationPercentComplete = ((plannedDuration - remainingDuration) / plannedDuration) * 100.0;
            if (durationPercentComplete < 0.0) {
                durationPercentComplete = 0;
            }
            else {
                if (durationPercentComplete > 100) {
                    durationPercentComplete = 100;
                }
            }
            parentTask.percent_complete = Math.round(durationPercentComplete * 10000) / 10000;
            parentTask.percent_complete_type = PercentCompleteType.DURATION;
        }
        // Force total slack calculation to avoid overwriting the critical flag
        // parentTask.getTotalSlack(); unsure how this forces total slack calculation
        parentTask.critical = critical;
    }
    /**
     * Date fields the forward/backward/LOE/WBS passes own.
     * Duration, percent complete, and critical are derived on WBS rollup; activities keep P6 values.
     */
    static CPM_OUTPUT_FIELDS = [
        'start',
        'finish',
        'early_start',
        'early_finish',
        'late_start',
        'late_finish',
        'remaining_early_start',
        'remaining_early_finish',
        'remaining_late_start',
        'remaining_late_finish',
        'planned_start',
        'planned_finish',
        'actual_start',
        'actual_finish',
        'duration',
        'actual_duration',
        'remaining_duration',
    ];
    static CPM_DATE_FIELDS = new Set([
        'start',
        'finish',
        'early_start',
        'early_finish',
        'late_start',
        'late_finish',
        'remaining_early_start',
        'remaining_early_finish',
        'remaining_late_start',
        'remaining_late_finish',
        'planned_start',
        'planned_finish',
        'actual_start',
        'actual_finish',
    ]);
    static CPM_DURATION_FIELDS = new Set([
        'duration',
        'actual_duration',
        'remaining_duration',
    ]);
    /** Compare computed copies to the pre-CPM docs, matching Java getDeltas + JsonWriter omit-null. */
    collectDeltas(originalTasks) {
        this.taskDeltas.clear();
        const originalById = new Map(originalTasks.map((task) => [task.unique_id, task]));
        for (const computed of this.m_tasks) {
            const original = originalById.get(computed.unique_id);
            if (!original)
                continue;
            for (const field of CommonScheduleCalculate.CPM_OUTPUT_FIELDS) {
                const oldValue = original[field];
                const newValue = computed[field];
                if (!this.isCpmFieldChanged(field, oldValue, newValue))
                    continue;
                this.registerDelta(computed.unique_id, {
                    field,
                    old_value: oldValue,
                    new_value: newValue,
                });
            }
        }
    }
    isCpmFieldChanged(field, oldValue, newValue) {
        if (this.isEmptyCpmValue(oldValue) && this.isEmptyCpmValue(newValue)) {
            return false;
        }
        // JsonWriter omits null/false/empty; do not wipe stored values.
        if (this.isEmptyCpmValue(newValue)) {
            return false;
        }
        if (newValue === false && this.isEmptyCpmValue(oldValue)) {
            return false;
        }
        if (CommonScheduleCalculate.CPM_DATE_FIELDS.has(field)) {
            return this.isDifferentDate(oldValue, newValue);
        }
        if (CommonScheduleCalculate.CPM_DURATION_FIELDS.has(field)) {
            return this.isDifferentDuration(oldValue, newValue);
        }
        if (field === 'percent_complete') {
            return this.isDifferentPercent(oldValue, newValue);
        }
        return oldValue !== newValue;
    }
    isEmptyCpmValue(value) {
        return value == null || value === '';
    }
    /** Java ScheduleBase.isDifferentDuration: missing → 0 is not a change; ignore < 1 hour. */
    isDifferentDuration(oldValue, newValue) {
        const oldSeconds = this.toDurationSeconds(oldValue);
        const newSeconds = this.toDurationSeconds(newValue);
        if (oldSeconds == null && newSeconds == null)
            return false;
        if (oldSeconds == null && newSeconds === 0)
            return false;
        if (oldSeconds == null || newSeconds == null)
            return true;
        return Math.round(oldSeconds / DateUtils.SECONDS_PER_HOUR) !== Math.round(newSeconds / DateUtils.SECONDS_PER_HOUR);
    }
    toDurationSeconds(value) {
        if (value == null || value === '')
            return null;
        const seconds = Number(value);
        return Number.isFinite(seconds) ? seconds : null;
    }
    /** Stored percents are 4 decimal places; ignore IEEE remainder. */
    isDifferentPercent(oldValue, newValue) {
        const oldPercent = this.toDurationSeconds(oldValue);
        const newPercent = this.toDurationSeconds(newValue);
        if (oldPercent == null && newPercent == null)
            return false;
        if (oldPercent == null && newPercent === 0)
            return false;
        if (oldPercent == null || newPercent == null)
            return true;
        return Math.round(oldPercent * 10000) !== Math.round(newPercent * 10000);
    }
    /** Java ScheduleBase.isDifferentDate: more than one hour apart. */
    isDifferentDate(oldValue, newValue) {
        const oldDate = DateUtils.parseISO(typeof oldValue === 'string' ? oldValue : null);
        const newDate = DateUtils.parseISO(typeof newValue === 'string' ? newValue : null);
        if (oldDate == null && newDate == null)
            return false;
        if (oldDate == null || newDate == null)
            return true;
        return Math.abs(oldDate.getTime() - newDate.getTime()) > DateUtils.MILLIS_PER_HOUR;
    }
    registerDelta(unique_id, delta) {
        let existing = this.taskDeltas.get(unique_id) || [];
        this.taskDeltas.set(unique_id, [...existing, delta]);
    }
    /**
     * Schedule WBS Summary activities.
     */
    wbsSummaryPass() {
        const activities = this.m_tasks.filter(this.isWbsSummary);
        if (activities.length === 0) {
            return;
        }
        for (const activity of activities) {
            this.wbsSummaryPassTask(activity);
        }
    }
    isWbsSummary(task) {
        return task.activity_type == ActivityType.WBS_SUMMARY;
    }
    isTopLevelTask(task) {
        if (task.parent_task_unique_id == null || task.parent_task_unique_id === task.unique_id) {
            return true;
        }
        return this.getTask(task.parent_task_unique_id) == null;
    }
    getParentTask(task) {
        if (task.parent_task_unique_id == null || task.parent_task_unique_id === task.unique_id) {
            return null;
        }
        return this.getTask(task.parent_task_unique_id);
    }
    getChildTasks(task) {
        return this.m_tasks.filter((t) => t.parent_task_unique_id === task.unique_id && t.unique_id !== task.unique_id);
    }
    /**
     * Descend through the entire hierarchy beneath a WBS entry collecting all child tasks.
     *
     * @param wbs WBS entry
     * @param childTasks list to populated with child tasks
     * @return list of child tasks
     */
    getAllWbsDecendents(wbs) {
        const wbsChildTasks = this.getChildTasks(wbs);
        // Recursively collect from all child tasks and combine with current level
        const allChildTasks = [
            ...wbsChildTasks.filter((t) => !t.summary && t.activity_type != ActivityType.WBS_SUMMARY && t.actual_finish == null),
        ];
        for (const childTask of wbsChildTasks) {
            allChildTasks.push(...this.getAllWbsDecendents(childTask));
        }
        return allChildTasks;
    }
    wbsSummaryPassTask(task) {
        const parent = this.getParentTask(task);
        if (parent == null) {
            return;
        }
        // These values can be used directly from the parent WBS
        task.start = parent.start;
        task.finish = parent.finish;
        task.actual_start = parent.actual_start;
        task.actual_finish = parent.actual_finish;
        // These values can only be rolled up from not started or in progress child activities of the WBS
        // so we need to descend the wbs hierarchy find all child activities which match these criteria.
        const childTasks = this.getAllWbsDecendents(parent);
        // Calculate min early start from child tasks
        task.early_start = DateUtils.toIso(this.getMinTaskDate(childTasks, 'early_start'));
        // Calculate max early finish from child tasks
        task.early_finish = DateUtils.toIso(this.getMaxTaskDate(childTasks, 'early_finish'));
        // Calculate min remaining early start from child tasks
        task.remaining_early_start = DateUtils.toIso(this.getMinTaskDate(childTasks, 'remaining_early_start'));
        // Calculate max remaining early finish from child tasks
        task.remaining_early_finish = DateUtils.toIso(this.getMaxTaskDate(childTasks, 'remaining_early_finish'));
        // Calculate min late start from child tasks
        task.late_start = DateUtils.toIso(this.getMinTaskDate(childTasks, 'late_start'));
        // Calculate max late finish from child tasks
        task.late_finish = DateUtils.toIso(this.getMaxTaskDate(childTasks, 'late_finish'));
        // Calculate min remaining late start from child tasks
        task.remaining_late_start = DateUtils.toIso(this.getMinTaskDate(childTasks, 'remaining_late_start'));
        // Calculate max remaining late finish from child tasks
        task.remaining_late_finish = DateUtils.toIso(this.getMaxTaskDate(childTasks, 'remaining_late_finish'));
    }
    getMinTaskDate(tasks, dateField) {
        const dates = tasks
            .map((t) => DateUtils.parseISO(t[dateField]))
            .filter((d) => d != null);
        if (dates.length === 0)
            return null;
        return dates.reduce((a, b) => (a < b ? a : b));
    }
    getMaxTaskDate(tasks, dateField) {
        const dates = tasks
            .map((t) => DateUtils.parseISO(t[dateField]))
            .filter((d) => d != null);
        if (dates.length === 0)
            return null;
        return dates.reduce((a, b) => (a > b ? a : b));
    }
    debugDates(task) {
        var debugString = '==============================================\n';
        debugString += `debugDates ${task.name}\n`;
        debugString += `start ${task.start}\n`;
        debugString += `finish ${task.finish}\n`;
        debugString += `actual_start ${task.actual_start}\n`;
        debugString += `actual_finish ${task.actual_finish}\n`;
        debugString += `planned_start ${task.planned_start}\n`;
        debugString += `planned_finish ${task.planned_finish}\n`;
        debugString += `early_start ${task.early_start}\n`;
        debugString += `early_finish ${task.early_finish}\n`;
        debugString += `remaining_early_start ${task.remaining_early_start}\n`;
        debugString += `remaining_early_finish ${task.remaining_early_finish}\n`;
        debugString += `late_start ${task.late_start}\n`;
        debugString += `late_finish ${task.late_finish}\n`;
        debugString += `remaining_late_start ${task.remaining_late_start}\n`;
        debugString += `remaining_late_finish ${task.remaining_late_finish}\n`;
        console.log(debugString);
    }
}
